import { useAtom } from "jotai";
import { backendDataAtom } from "../atoms/backendDataAtom.js";
import {
  backendPost,
  backendPatch,
  backendDelete,
} from "@accredo/fe-shared/services/dataService.js";
import { useSnackbar } from "notistack";
import {
  updateMultiListItemState,
  removeMultiListItemState,
} from "./listStateHelpers.js";

// Paths into backendData for the different substitution lists
// backendData shape on this page:
// {
//   type: "substitution-page",
//   data: {
//     list: { data: [...], meta: {...} },
//     substituting_for: [...],
//     substituted_by: [...],
//   }
// }
const statePaths = [
  ["data", "list"],
  ["data", "substituting_for"],
  ["data", "substituted_by"],
];

export function useSubstitutionActions(onCountsChange) {
  const [backendData, setBackendData] = useAtom(backendDataAtom);
  const { enqueueSnackbar } = useSnackbar();

  // Full table state lives in backendData.data.list
  const state =
    backendData?.type === "substitution-page" && backendData?.data?.list
      ? backendData.data.list
      : { data: [], meta: {} };

  // Called from useMultiTableData.onStateChange(tableData)
  const setState = (tableData) => {
    setBackendData((prev) => {
      const prevData = prev?.type === "substitution-page" ? prev.data : {};
      return {
        ...(prev || {}),
        type: "substitution-page",
        data: {
          ...prevData,
          list: tableData,
        },
      };
    });
  };

  const createSubstitution = async (user_id, substitution_data) => {
    return await backendPost(`data/users/${user_id}/substitutions`, {
      label: "Stellvertretung erstellen",
      toSend: substitution_data,
      enqueueSnackbar,
      setResult: (res) => {
        const new_substitution = res?.data || res;

        // Add to full list optimistically if it is already loaded
        updateMultiListItemState(
          backendData,
          setBackendData,
          new_substitution,
          [["data", "list"]]
        );

        if (onCountsChange) {
          onCountsChange();
        }
      },
    });
  };

  const updateSubstitution = async (
    user_id,
    substitution_id,
    substitution_data
  ) => {
    return await backendPatch(
      `data/users/${user_id}/substitutions/${substitution_id}`,
      {
        label: "Stellvertretung aktualisieren",
        toSend: substitution_data,
        enqueueSnackbar,
        setResult: (res) => {
          const updated_substitution = res?.data || res;

          updateMultiListItemState(
            backendData,
            setBackendData,
            updated_substitution,
            statePaths
          );

          if (onCountsChange) {
            onCountsChange();
          }
        },
      }
    );
  };

  const deleteSubstitution = async (user_id, substitution_id) => {
    return await backendDelete(
      `data/users/${user_id}/substitutions/${substitution_id}`,
      {
        label: "Stellvertretung löschen",
        enqueueSnackbar,
        setResult: () => {
          // Remove from all substitution-related locations, if present
          removeMultiListItemState(
            backendData,
            setBackendData,
            substitution_id,
            statePaths
          );

          if (onCountsChange) {
            onCountsChange();
          }
        },
      }
    );
  };

  // Expose compact state data for reading
  const compactState =
    backendData?.type === "substitution-page" && backendData?.data
      ? {
          substituting_for: backendData.data.substituting_for || [],
          substituted_by: backendData.data.substituted_by || [],
        }
      : {
          substituting_for: [],
          substituted_by: [],
        };

  const setCompactState = (scope, data) => {
    setBackendData((prev) => {
      const prevData = prev?.type === "substitution-page" ? prev.data : {};
      return {
        ...(prev || {}),
        type: "substitution-page",
        data: {
          ...prevData,
          [scope]: data,
        },
      };
    });
  };

  return {
    createSubstitution,
    updateSubstitution,
    deleteSubstitution,
    setState,
    state,
    compactState,
    setCompactState,
  };
}
